import { environment } from '@ieEnv/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  CreateOrgDto,
  OrgResponseDto,
  UpdateOrgDto,
} from 'shared-general-libs/dto/org';
import { PaginatedResponseDto } from 'shared-general-libs/dto/pagination';
import { PaginationInterface } from '@ieShared/interfaces/pagination.interface';
import { removeEmptyFields } from '@ieShared/utils/query.utils';
import {
  EnumDetailsResponseDto,
  UpdateEnumDto,
} from 'shared-general-libs/dto/org';
import { BYPASS_FORBIDDEN_REDIRECT } from '@ieCore/interceptors/http-context-tokens';
import { UpdateOrgFeatureFlagsPayload } from '@ieShared/interfaces/organization.interface';

@Injectable({
  providedIn: 'root',
})
export class OrganizationRepository {
  private readonly apiUrl: string = environment.organizationHost;

  constructor(private http: HttpClient) {}

  getCompanies(
    params: PaginationInterface,
  ): Observable<PaginatedResponseDto<OrgResponseDto>> {
    return this.http.get<PaginatedResponseDto<OrgResponseDto>>(
      `${this.apiUrl}/orgs`,
      { params: removeEmptyFields(params) },
    );
  }

  getCompany(
    companyId: string,
    opts?: { bypassForbiddenRedirect?: boolean },
  ): Observable<OrgResponseDto> {
    const context = new HttpContext().set(
      BYPASS_FORBIDDEN_REDIRECT,
      opts?.bypassForbiddenRedirect === true,
    );
    return this.http.get<OrgResponseDto>(`${this.apiUrl}/orgs/${companyId}`, {
      context,
    });
  }

  getEnums(companyId: string): Observable<EnumDetailsResponseDto[]> {
    return this.http.get<EnumDetailsResponseDto[]>(
      `${this.apiUrl}/enum/${companyId}`,
    );
  }

  getEnumTranslationList(): Observable<{ [p: string]: string }> {
    return this.http.get<{ [p: string]: string }>(
      `${this.apiUrl}/enum/translations`,
    );
  }

  getEnumList(key: string): Observable<{ [key: string]: string }> {
    const encodedKey = key
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    return this.http.get<{ [key: string]: string }>(
      `${this.apiUrl}/enum/${encodedKey}`,
    );
  }

  updateEnums(
    orgId: string,
    enumId: string,
    keyPairs: UpdateEnumDto,
  ): Observable<EnumDetailsResponseDto> {
    return this.http.put<EnumDetailsResponseDto>(
      `${this.apiUrl}/enum/${orgId}/${enumId}`,
      keyPairs,
    );
  }

  createCompany(data: CreateOrgDto): Observable<OrgResponseDto> {
    return this.http.post<OrgResponseDto>(`${this.apiUrl}/orgs`, data);
  }

  updateCompany(
    companyId: string,
    data: UpdateOrgDto,
  ): Observable<OrgResponseDto> {
    return this.http.put<OrgResponseDto>(
      `${this.apiUrl}/orgs/${companyId}`,
      data,
    );
  }

  deleteCompany(companyId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/orgs/${companyId}`);
  }

  updateOrgFeatureFlags(
    orgId: string,
    flags: UpdateOrgFeatureFlagsPayload,
  ): Observable<OrgResponseDto> {
    return this.http.patch<OrgResponseDto>(
      `${this.apiUrl}/orgs/${orgId}/feature-flags`,
      flags,
    );
  }
}
